import { create } from 'zustand';

interface AgentChunkAccordionState {
  collapsedByScope: Record<string, string[]>;
  expandedSourcesByScope: Record<string, string[]>;
  initializedCollapsedScopes: Record<string, true>;
  toggleSection: (scopeId: string, sectionKey: string, currentlyExpanded: boolean) => void;
  initializeCollapsedSections: (scopeId: string, sectionKeys: string[]) => void;
  toggleSources: (scopeId: string, sectionKey: string) => void;
  resetScope: (scopeId: string) => void;
  resetAll: () => void;
}

interface AgentChunkAccordionToolCallLike {
  id?: string;
  agent?: string;
  content?: string;
  status?: string;
}

export const getAgentChunkAccordionSectionKey = (
  call: AgentChunkAccordionToolCallLike,
  index: number,
) => {
  const name = (call.agent || '').trim();
  return call.id || `${name}-${index}`;
};

/**
 * Client-only UI state for agent stream/result accordions.
 *
 * Sections are derived as open by default unless a caller initializes a scope
 * as collapsed. This keeps live streams open while allowing finalized result
 * accordions to start closed and avoid duplicating the final answer.
 */
export const useAgentChunkAccordionStore = create<AgentChunkAccordionState>((set) => ({
  collapsedByScope: {},
  expandedSourcesByScope: {},
  initializedCollapsedScopes: {},

  toggleSection: (scopeId, sectionKey, currentlyExpanded) => {
    set((state) => {
      const collapsedKeys = new Set(state.collapsedByScope[scopeId] || []);

      if (currentlyExpanded) {
        collapsedKeys.add(sectionKey);
      } else {
        collapsedKeys.delete(sectionKey);
      }

      return {
        collapsedByScope: {
          ...state.collapsedByScope,
          [scopeId]: Array.from(collapsedKeys),
        },
        initializedCollapsedScopes: {
          ...state.initializedCollapsedScopes,
          [scopeId]: true,
        },
      };
    });
  },

  initializeCollapsedSections: (scopeId, sectionKeys) => {
    if (!scopeId || sectionKeys.length === 0) return;

    set((state) => {
      if (state.initializedCollapsedScopes[scopeId]) {
        return {};
      }

      const collapsedKeys = new Set(state.collapsedByScope[scopeId] || []);
      sectionKeys.forEach((sectionKey) => {
        if (sectionKey) {
          collapsedKeys.add(sectionKey);
        }
      });

      return {
        collapsedByScope: {
          ...state.collapsedByScope,
          [scopeId]: Array.from(collapsedKeys),
        },
        initializedCollapsedScopes: {
          ...state.initializedCollapsedScopes,
          [scopeId]: true,
        },
      };
    });
  },

  toggleSources: (scopeId, sectionKey) => {
    set((state) => {
      const expandedSourcesByScope = state.expandedSourcesByScope || {};
      const expandedKeys = new Set(expandedSourcesByScope[scopeId] || []);

      if (expandedKeys.has(sectionKey)) {
        expandedKeys.delete(sectionKey);
      } else {
        expandedKeys.add(sectionKey);
      }

      return {
        expandedSourcesByScope: {
          ...expandedSourcesByScope,
          [scopeId]: Array.from(expandedKeys),
        },
      };
    });
  },

  resetScope: (scopeId) => {
    set((state) => {
      const collapsedByScope = { ...(state.collapsedByScope || {}) };
      const expandedSourcesByScope = { ...(state.expandedSourcesByScope || {}) };
      const initializedCollapsedScopes = { ...(state.initializedCollapsedScopes || {}) };
      delete collapsedByScope[scopeId];
      delete expandedSourcesByScope[scopeId];
      delete initializedCollapsedScopes[scopeId];
      return { collapsedByScope, expandedSourcesByScope, initializedCollapsedScopes };
    });
  },

  resetAll: () => {
    set({
      collapsedByScope: {},
      expandedSourcesByScope: {},
      initializedCollapsedScopes: {},
    });
  },
}));

export type { AgentChunkAccordionState };
